import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { RETAIL_MIN_COLUMN_WIDTH_PX } from '@/components/retail/retailTableLayout';

/** Narrow hit strip around a column's right edge (full table height). */
const COLUMN_RESIZE_HANDLE_PX = 8;

const RESIZE_COLGROUP_ATTR = 'data-retail-resize-cols';
const RESIZE_LAYER_ATTR = 'data-retail-resize-layer';
const RESIZE_GUIDE_ATTR = 'data-retail-resize-guide';

type ColumnWidthMap = Record<string, number>;

function retailResizeMinWidthPx(headerMinWidthPx: number): number {
  return Math.max(RETAIL_MIN_COLUMN_WIDTH_PX, Math.ceil(headerMinWidthPx));
}

/** Intrinsic header width with truncate/overflow lifted so labels stay fully visible. */
function measureRetailHeaderMinWidthPx(th: HTMLElement): number {
  const table = document.createElement('table');
  const thead = document.createElement('thead');
  const row = document.createElement('tr');
  const probe = th.cloneNode(true) as HTMLElement;
  probe.removeAttribute('id');
  probe.style.width = 'auto';
  probe.style.maxWidth = 'none';
  probe.style.minWidth = '0';
  probe.style.overflow = 'visible';
  probe.style.whiteSpace = 'nowrap';
  probe.querySelectorAll<HTMLElement>('*').forEach((el) => {
    el.style.overflow = 'visible';
    el.style.textOverflow = 'clip';
    el.style.whiteSpace = 'nowrap';
    el.style.maxWidth = 'none';
    el.style.width = 'auto';
    el.style.minWidth = '0';
    el.style.flex = '0 0 auto';
  });
  table.style.position = 'absolute';
  table.style.left = '-9999px';
  table.style.top = '0';
  table.style.visibility = 'hidden';
  table.style.borderCollapse = 'collapse';
  table.style.tableLayout = 'auto';
  row.appendChild(probe);
  thead.appendChild(row);
  table.appendChild(thead);
  document.body.appendChild(table);
  const width = Math.ceil(probe.getBoundingClientRect().width);
  table.remove();
  return retailResizeMinWidthPx(width);
}

function applyRetailHeaderMinWidths(table: HTMLTableElement) {
  const headers = Array.from(
    table.querySelectorAll<HTMLElement>('thead th[data-retail-col-id]')
  );
  headers.forEach((th, index) => {
    const min = measureRetailHeaderMinWidthPx(th);
    const minPx = `${min}px`;
    if (th.style.minWidth !== minPx) th.style.minWidth = minPx;
    table.querySelectorAll('tbody tr').forEach((row) => {
      const cell = row.children[index] as HTMLElement | undefined;
      if (cell && cell.style.minWidth !== minPx) cell.style.minWidth = minPx;
    });
  });
}

function headerColumnKey(th: HTMLElement, index: number): string {
  return th.dataset.retailColId || `__idx-${index}`;
}

function snapshotTableColumnWidths(table: HTMLTableElement, existing: ColumnWidthMap): ColumnWidthMap {
  const widths: ColumnWidthMap = { ...existing };
  table.querySelectorAll<HTMLElement>('thead th').forEach((th, index) => {
    const key = headerColumnKey(th, index);
    if (widths[key] == null) {
      widths[key] = Math.round(th.getBoundingClientRect().width);
    }
  });
  return widths;
}

function columnResizeMinPx(th: HTMLElement): number {
  if (!th.dataset.retailColId) return RETAIL_MIN_COLUMN_WIDTH_PX;
  return measureRetailHeaderMinWidthPx(th);
}

function syncRetailTableColumnWidths(table: HTMLTableElement, widths: ColumnWidthMap) {
  const headers = Array.from(table.querySelectorAll<HTMLElement>('thead th'));
  if (headers.length === 0) return;

  let colgroup = table.querySelector(`colgroup[${RESIZE_COLGROUP_ATTR}]`);
  if (!colgroup) {
    colgroup = document.createElement('colgroup');
    colgroup.setAttribute(RESIZE_COLGROUP_ATTR, '');
    table.insertBefore(colgroup, table.firstChild);
  }

  while (colgroup.children.length < headers.length) {
    colgroup.appendChild(document.createElement('col'));
  }
  while (colgroup.children.length > headers.length) {
    colgroup.lastChild?.remove();
  }

  let total = 0;
  headers.forEach((th, index) => {
    const key = headerColumnKey(th, index);
    const raw = widths[key] ?? Math.round(th.getBoundingClientRect().width);
    const width = Math.max(columnResizeMinPx(th), raw);
    const col = colgroup!.children[index] as HTMLTableColElement;
    col.style.width = `${width}px`;
    col.style.minWidth = `${width}px`;
    col.style.maxWidth = `${width}px`;
    total += width;
  });

  table.style.tableLayout = 'fixed';
  table.style.width = `${total}px`;
  table.style.minWidth = `${total}px`;
  table.style.setProperty('width', `${total}px`, 'important');
  table.style.setProperty('min-width', `${total}px`, 'important');
  table.style.setProperty('max-width', 'none', 'important');
}

function ensureResizeGuide(): HTMLDivElement {
  let guide = document.querySelector<HTMLDivElement>(`[${RESIZE_GUIDE_ATTR}]`);
  if (guide) return guide;
  guide = document.createElement('div');
  guide.setAttribute(RESIZE_GUIDE_ATTR, '');
  guide.setAttribute('aria-hidden', 'true');
  Object.assign(guide.style, {
    position: 'fixed',
    width: '1px',
    pointerEvents: 'none',
    zIndex: '10000',
    background: 'var(--brand-primary)',
    display: 'none',
  });
  document.body.appendChild(guide);
  return guide;
}

function showResizeGuide(viewport: HTMLElement, clientX: number) {
  const guide = ensureResizeGuide();
  const rect = viewport.getBoundingClientRect();
  guide.style.left = `${clientX}px`;
  guide.style.top = `${rect.top}px`;
  guide.style.height = `${Math.max(rect.height, 1)}px`;
  guide.style.display = 'block';
}

function hideResizeGuide() {
  const guide = document.querySelector<HTMLDivElement>(`[${RESIZE_GUIDE_ATTR}]`);
  if (guide) guide.style.display = 'none';
}

function syncRetailResizeHandles(
  table: HTMLTableElement,
  viewport: HTMLElement,
  onResizeStart: (columnId: string, startWidth: number, clientX: number) => void
) {
  if (getComputedStyle(viewport).position === 'static') {
    viewport.style.position = 'relative';
  }

  let layer = viewport.querySelector<HTMLDivElement>(`:scope > [${RESIZE_LAYER_ATTR}]`);
  if (!layer) {
    layer = document.createElement('div');
    layer.setAttribute(RESIZE_LAYER_ATTR, '');
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      pointerEvents: 'none',
      zIndex: '60',
    });
    viewport.appendChild(layer);
  }

  layer.style.left = `${table.offsetLeft}px`;
  layer.style.top = `${table.offsetTop}px`;
  layer.style.width = `${table.offsetWidth}px`;
  layer.style.height = `${table.offsetHeight}px`;

  const headers = Array.from(table.querySelectorAll<HTMLElement>('thead th[data-retail-col-id]'));
  const tableRect = table.getBoundingClientRect();

  while (layer.childElementCount > headers.length) {
    layer.lastElementChild?.remove();
  }
  while (layer.childElementCount < headers.length) {
    const handle = document.createElement('div');
    handle.dataset.retailColResizeHandle = '';
    Object.assign(handle.style, {
      position: 'absolute',
      top: '0',
      width: `${COLUMN_RESIZE_HANDLE_PX}px`,
      height: '100%',
      pointerEvents: 'auto',
      cursor: 'col-resize',
    });
    layer.appendChild(handle);
  }

  headers.forEach((th, index) => {
    const columnId = th.dataset.retailColId;
    const handle = layer!.children[index] as HTMLDivElement;
    if (!columnId) {
      handle.style.display = 'none';
      return;
    }
    const thRect = th.getBoundingClientRect();
    const left = thRect.right - tableRect.left - COLUMN_RESIZE_HANDLE_PX / 2;
    handle.style.display = 'block';
    handle.style.left = `${left}px`;
    handle.onmousedown = (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      onResizeStart(columnId, Math.round(thRect.width), event.clientX);
    };
  });
}

/**
 * Retail-only column resize: header min-widths and full-height resize handles.
 */
export function useRetailTableResize(columnSignature = ''): {
  tableRef: (node: HTMLTableElement | null) => void;
} {
  const [table, setTable] = useState<HTMLTableElement | null>(null);
  const [columnWidths, setColumnWidths] = useState<ColumnWidthMap>({});
  const columnWidthsRef = useRef<ColumnWidthMap>({});
  const tableRef = useCallback((node: HTMLTableElement | null) => {
    setTable(node);
  }, []);

  useLayoutEffect(() => {
    columnWidthsRef.current = columnWidths;
  }, [columnWidths]);

  useLayoutEffect(() => {
    if (!table) return;
    if (Object.keys(columnWidths).length === 0) return;
    syncRetailTableColumnWidths(table, columnWidths);
  }, [table, columnSignature, columnWidths]);

  useLayoutEffect(() => {
    if (!table) return;

    const measure = () => {
      if (Object.keys(columnWidthsRef.current).length > 0) {
        syncRetailTableColumnWidths(table, columnWidthsRef.current);
      } else {
        applyRetailHeaderMinWidths(table);
      }
    };

    const resizeObserver = new ResizeObserver(measure);
    const observeHeaderCells = () => {
      table.querySelectorAll('thead th').forEach((el) => {
        resizeObserver.observe(el);
      });
      measure();
    };

    observeHeaderCells();
    const mutationObserver = new MutationObserver(() => {
      observeHeaderCells();
    });
    if (table.tHead) {
      mutationObserver.observe(table.tHead, { childList: true, subtree: true });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [table, columnSignature]);

  useLayoutEffect(() => {
    if (!table) return;
    const viewport = table.parentElement;
    if (!viewport) return;

    type DragState = { columnId: string; startX: number; startWidth: number };
    let drag: DragState | null = null;

    const refreshHandles = () => {
      syncRetailResizeHandles(table, viewport, (columnId, startWidth, clientX) => {
        const widths = snapshotTableColumnWidths(table, columnWidthsRef.current);
        drag = {
          columnId,
          startX: clientX,
          startWidth: widths[columnId] ?? startWidth,
        };
        columnWidthsRef.current = widths;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        showResizeGuide(viewport, clientX);
      });
    };

    refreshHandles();

    const columnMinWidths = new Map<string, number>();
    table.querySelectorAll<HTMLElement>('thead th[data-retail-col-id]').forEach((th) => {
      const id = th.dataset.retailColId;
      if (id) columnMinWidths.set(id, measureRetailHeaderMinWidthPx(th));
    });

    const onMouseMove = (event: MouseEvent) => {
      if (!drag) return;
      const minWidth = columnMinWidths.get(drag.columnId) ?? RETAIL_MIN_COLUMN_WIDTH_PX;
      const nextWidth = Math.max(
        minWidth,
        Math.round(drag.startWidth + (event.clientX - drag.startX))
      );
      const next = {
        ...columnWidthsRef.current,
        [drag.columnId]: nextWidth,
      };
      columnWidthsRef.current = next;
      syncRetailTableColumnWidths(table, next);
      const th = table.querySelector<HTMLElement>(
        `thead th[data-retail-col-id="${CSS.escape(drag.columnId)}"]`
      );
      showResizeGuide(viewport, th ? Math.round(th.getBoundingClientRect().right) : event.clientX);
      document.body.style.cursor = 'col-resize';
    };

    const onMouseUp = () => {
      if (!drag) return;
      drag = null;
      setColumnWidths({ ...columnWidthsRef.current });
      hideResizeGuide();
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      refreshHandles();
    };

    const handleObserver = new ResizeObserver(() => {
      if (drag) return;
      refreshHandles();
    });
    handleObserver.observe(table);
    viewport.addEventListener(
      'scroll',
      () => {
        if (drag) return;
        refreshHandles();
      },
      { passive: true }
    );
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    const mutationObserver = new MutationObserver(() => {
      refreshHandles();
    });
    if (table.tHead) {
      mutationObserver.observe(table.tHead, { childList: true, subtree: true });
    }

    return () => {
      handleObserver.disconnect();
      mutationObserver.disconnect();
      viewport.removeEventListener('scroll', refreshHandles);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      viewport.querySelector(`:scope > [${RESIZE_LAYER_ATTR}]`)?.remove();
      hideResizeGuide();
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    };
  }, [table, columnSignature]);

  return { tableRef };
}
